import { RequestStatus, entities } from 'typed/api';
import { PerformanceIndicatorPointCalculation, PerformanceIndicatorComparisonTarget } from 'utils/constants';
import { deepAssign, PeriodType, Period, PeriodRange } from 'typed/utils';
import DateRange from 'utils/DateRange';

const csLinkProps: (keyof entities.CorporateStructureLink)[] = ['ProductID', 'GeoAreaID', 'CustomerID', 'TrackingLevel1ItemID', 'TrackingLevel2ItemID', 'TrackingLevel3ItemID', 'TrackingLevel4ItemID', 'CompanyLegalEntityID', 'CompanyFunctionID'];
export function stringifyCorporateStructureLink(link: Partial<entities.CorporateStructureLink>) {
    return csLinkProps.map(propName => link[propName] || 0).join('_');
}

// TODO findIndicatorData shouldn't support PerformanceIndicatorAvailabilityReport by design.
//      For now we have made it to support them by adding a fully loaded loadedRanges to defaults.PerformanceIndicatorAvailabilityReport.
export function findIndicatorData(indicatorDatas: entities.PerformanceIndicatorDataReport[], filter: entities.PerformanceIndicatorDataFilter, adjustStatus: boolean = false): entities.PerformanceIndicatorDataReport | undefined {
    const
        data = indicatorDatas.getByID(`${filter.IndicatorID}_${filter.PeriodTypeID || 0}_${stringifyCorporateStructureLink(filter.CorporateStructureLink)}`),

        ninf = -Infinity as unknown as Date,
        pinf = Infinity as unknown as Date,
        empty = { start: pinf, end: ninf, status: data ? data.Custom.status : RequestStatus.SUCCEEDED },

        fs = filter.StartDate || ninf,
        fe = filter.EndDate || pinf,

        { start: rs, end: re, status } = (data && data.Custom.loadedRanges || []).reduce<typeof empty>((range, { start, end, status }) => {
            const
                ss = start || ninf,
                se = end || pinf;

            if (
                fs <= ss && ss <= fe || fs <= se && se <= fe ||
                ss <= fs && fs <= se || ss <= fe && fe <= se
            ) {
                if (ss <= range.start && (se >= range.start || se >= range.end)) {
                    range.start = ss;
                }

                if ((ss <= range.end || ss <= range.start) && se >= range.end) {
                    range.end = se;
                }

                if (adjustStatus && status !== RequestStatus.SUCCEEDED) {
                    range.status = status;
                }
            }

            return range;
        }, empty);

    return data && rs <= fs && fe <= re ?
        deepAssign(data, { Custom: { status } }) :
        undefined;
}

const YEARS_AGO_MAP: { [key: string]: number } = {
    [PerformanceIndicatorComparisonTarget.PREVIOUS_YEAR]: -1,
    [PerformanceIndicatorComparisonTarget.TWO_YEARS_AGO]: -2,
    [PerformanceIndicatorComparisonTarget.THREE_YEARS_AGO]: -3
};

// creates a reasonable StartDate and EndDate to load indicator data for a table/chart with period selection:
// it should split the whole possible date range into fixed clusters and map every date to a cluster 
//      this way the filter range won't change when user goes from Feb to Mar, but only change when user goes through a cluster border, e. g. Dec to Jan
// and it should expand every cluster to preload a bit of data from previous and next cluster
//      so when user goes from Dec to Jan to Feb to... we have some data to show while we are loading the full cluster in background
// for now it's half of higher period (half-year for months etc) + 2 extra periods
export function createIndicatorDataRange(visibleRange: PeriodRange | IDateRange | Period, pointCalculationID: number | null = null, fiscalPeriodStartMonth: number = 1, comparisonTargetID: number | null = null): entities.PerformanceIndicatorDataFilterRange {
    const type: PeriodType =
        visibleRange instanceof DateRange ? ((visibleRange as IDateRange).periodType as any) as PeriodType :
            visibleRange instanceof PeriodRange ? (visibleRange as PeriodRange).type :
                (visibleRange as Period).type;
    const first: Date = visibleRange instanceof DateRange ? ((visibleRange as IDateRange).first as any) as Date :
        visibleRange instanceof PeriodRange ? (visibleRange as PeriodRange).first() :
            (visibleRange as Period);
    const last: Date = visibleRange instanceof DateRange ? ((visibleRange as IDateRange).last as any) as Date :
        visibleRange instanceof PeriodRange ? (visibleRange as PeriodRange).last() :
            (visibleRange as Period);

    let clustersStartPeriod: Period | null = new Period(type, first);
    let clustersEndPeriod: Period | null = new Period(type, last);

    if (clustersStartPeriod) {
        switch (pointCalculationID || PerformanceIndicatorPointCalculation.VALUE) {
            case PerformanceIndicatorPointCalculation.CHANGE:
                clustersStartPeriod = clustersStartPeriod.add(-1);
                break;

            case PerformanceIndicatorPointCalculation.ROLLING:
                clustersStartPeriod = new DateRange(type, clustersStartPeriod).first;
                break;

            case PerformanceIndicatorPointCalculation.CUMULATIVE:
            case PerformanceIndicatorPointCalculation.TOTAL:
                clustersStartPeriod = null;
                break;

            case PerformanceIndicatorPointCalculation.YEAR_CUMULATIVE:
            case PerformanceIndicatorPointCalculation.FISCAL_PERIOD_CUMULATIVE:
            case PerformanceIndicatorPointCalculation.YEAR_TOTAL:
            case PerformanceIndicatorPointCalculation.FISCAL_PERIOD_TOTAL:
                const firstMonth =
                    pointCalculationID !== PerformanceIndicatorPointCalculation.FISCAL_PERIOD_CUMULATIVE &&
                    pointCalculationID !== PerformanceIndicatorPointCalculation.FISCAL_PERIOD_TOTAL ?
                        0 :
                        fiscalPeriodStartMonth - 1;
                if (clustersStartPeriod.getUTCMonth() < firstMonth) {
                    clustersStartPeriod.setUTCFullYear(clustersStartPeriod.getUTCFullYear() - 1);
                }
                clustersStartPeriod.setUTCMonth(firstMonth);
                clustersStartPeriod.setUTCDate(1);
                break;

            case PerformanceIndicatorPointCalculation.LAST_TWELVE_MONTHS:
                if (clustersEndPeriod) {
                    clustersEndPeriod = +clustersEndPeriod.add(-11).start < +clustersStartPeriod.start ? clustersStartPeriod : clustersEndPeriod.add(-11);
                };

                clustersStartPeriod = clustersStartPeriod.add(-11);
                break;
        }
    }

    if (clustersEndPeriod) {
        switch (pointCalculationID || PerformanceIndicatorPointCalculation.VALUE) {
            case PerformanceIndicatorPointCalculation.TOTAL:
                clustersEndPeriod = null;
                break;

            case PerformanceIndicatorPointCalculation.YEAR_TOTAL:
            case PerformanceIndicatorPointCalculation.FISCAL_PERIOD_TOTAL:
                const firstMonth = pointCalculationID !== PerformanceIndicatorPointCalculation.FISCAL_PERIOD_TOTAL ? 0 : fiscalPeriodStartMonth - 1;
                if (clustersEndPeriod.getUTCMonth() < firstMonth) {
                    clustersEndPeriod.setUTCFullYear(clustersEndPeriod.getUTCFullYear() - 1);
                }
                clustersEndPeriod.setUTCFullYear(clustersEndPeriod.getUTCFullYear() + 1);
                clustersEndPeriod.setUTCMonth(firstMonth);
                clustersEndPeriod.setUTCDate(1);
                break;
        }
    }

    if (clustersStartPeriod) {
        switch (comparisonTargetID) {
            case PerformanceIndicatorComparisonTarget.PREVIOUS:
                clustersStartPeriod = clustersStartPeriod.add(-1);
                break;

            case PerformanceIndicatorComparisonTarget.PREVIOUS_YEAR:
            case PerformanceIndicatorComparisonTarget.TWO_YEARS_AGO:
            case PerformanceIndicatorComparisonTarget.THREE_YEARS_AGO:
                if (type !== PeriodType.WEEK) {
                    clustersStartPeriod.setUTCFullYear(clustersStartPeriod.getUTCFullYear() + YEARS_AGO_MAP[comparisonTargetID as number]);
                }
                else {
                    clustersStartPeriod = new Period(type, Date.fromUTCWeek(clustersStartPeriod.getUTCWeeklyYear() - 1, clustersStartPeriod.getUTCWeek()));
                }
                break;
        }
    }

    switch (type) {
        case PeriodType.DAY:
            clustersStartPeriod && clustersStartPeriod.setUTCDate(Math.floor(clustersStartPeriod.getUTCDate() / 15) * 15);
            clustersEndPeriod && clustersEndPeriod.setUTCDate(Math.ceil(clustersEndPeriod.getUTCDate() / 15) * 15);
            break;

        case PeriodType.WEEK:
            clustersStartPeriod && clustersStartPeriod.setUTCWeek(Math.floor(clustersStartPeriod.getUTCWeek() / 5) * 5);
            clustersEndPeriod && clustersEndPeriod.setUTCWeek(Math.ceil(clustersEndPeriod.getUTCWeek() / 5) * 5);
            break;

        case PeriodType.MONTH:
            clustersStartPeriod && clustersStartPeriod.setUTCMonth(Math.floor(clustersStartPeriod.getUTCMonth() / 6) * 6);
            clustersEndPeriod && clustersEndPeriod.setUTCMonth(Math.ceil(clustersEndPeriod.getUTCMonth() / 6) * 6);
            break;

        case PeriodType.QUARTER:
            clustersStartPeriod && clustersStartPeriod.setUTCQuarter(Math.floor(clustersStartPeriod.getUTCQuarter() / 4) * 4);
            clustersEndPeriod && clustersEndPeriod.setUTCQuarter(Math.ceil(clustersEndPeriod.getUTCQuarter() / 4) * 4);
            break;

        case PeriodType.YEAR:
            clustersStartPeriod && clustersStartPeriod.setUTCFullYear(Math.floor(clustersStartPeriod.getUTCFullYear() / 3) * 3);
            clustersEndPeriod && clustersEndPeriod.setUTCFullYear(Math.ceil(clustersEndPeriod.getUTCFullYear() / 3) * 3);
            break;

        default:
            throw new Error(`Unknown period type ${type}.`);
    }
    return {
        StartDate: clustersStartPeriod ? clustersStartPeriod.add(-2) : undefined,
        EndDate: clustersEndPeriod ? clustersEndPeriod.add(3) : undefined // +1 to make sure that neighbour clusters have some overlap and no gaps less than a day between them
    };
}

export function indicatorDrillDataID(filters: any): string {
    return `${filters.IndicatorID}_${filters.PeriodTypeID || 0}_${filters.PeriodStart.getTime() || 0}_${filters.PeriodEnd.getTime() || 0}_${filters.DimensionID || 0}_${filters.PointCalculationID || 0}_${stringifyCorporateStructureLink(filters.CorporateStructureLink)}`;
}